/**
 * In-memory Graph query log (ported from x365 graph.js logQuery).
 *
 * Two jobs:
 *  1. Powers the "Query Log" view — the transparency proof that the app only ever
 *     talks to Microsoft Graph (zero-egress, MERGE-PLAN §2).
 *  2. Supplies evidence provenance — the endpoint + status + timestamp stamped onto
 *     every compliance evidence record (MERGE-PLAN §4.2).
 *
 * Bounded ring buffer, memory only. Nothing is persisted or sent anywhere.
 */

export interface QueryLogEntry {
  id: number;
  /** Graph path or absolute URL requested, e.g. '/users' or '/$batch'. */
  url: string;
  method: string;
  status: number | 'error';
  durationMs: number;
  at: string; // ISO timestamp
}

const MAX_ENTRIES = 500;
let entries: QueryLogEntry[] = [];
let seq = 0;
const listeners = new Set<(entries: readonly QueryLogEntry[]) => void>();

function emit() {
  const snapshot = entries;
  for (const l of listeners) l(snapshot);
}

/** Record one Graph call. Returns the entry (handy for tests). */
export function logQuery(
  url: string,
  method: string,
  status: number | 'error',
  durationMs: number,
): QueryLogEntry {
  const entry: QueryLogEntry = {
    id: ++seq,
    url,
    method,
    status,
    durationMs: Math.round(durationMs),
    at: new Date().toISOString(),
  };
  entries = [entry, ...entries].slice(0, MAX_ENTRIES);
  emit();
  return entry;
}

export function getQueryLog(): readonly QueryLogEntry[] {
  return entries;
}

/** Most recent successful call whose URL contains `match` — evidence provenance. */
export function findProvenance(match: string): QueryLogEntry | undefined {
  return entries.find((e) => e.url.includes(match) && e.status !== 'error');
}

export function subscribeQueryLog(fn: (entries: readonly QueryLogEntry[]) => void): () => void {
  listeners.add(fn);
  fn(entries);
  return () => listeners.delete(fn);
}

export function clearQueryLog(): void {
  entries = [];
  seq = 0;
  emit();
}
